import { useCallback, useEffect, useRef, useState } from "react";
import { LIMITS } from "../../../shared/constants";
import { mapLimit } from "../../../shared/page-images";
import type { importApi } from "../../api/queries";
import type { PreparedPage } from "./pages";

// State of the browser-side preparation shown in the page preview (Import page and onboarding): documents are
// prepared as soon as they are chosen or photographed, the preview fills page by page, more pages can be added
// (the next photo of the menu), reordered, turned, removed or photographed again, and the upload sends the
// included pages in the preview's order. The preparation module (and pdf.js inside it) loads only when needed.

export type PrepPhase = "idle" | "preparing" | "ready" | "uploading" | "error";

/**
 * Where the documents came from: the phone camera (one photo at a time), photos already on the phone (several
 * at once) or files (PDFs or images from the disk, a drop).
 */
export type PrepSource = "camera" | "gallery" | "files";

export interface PrepState {
  phase: PrepPhase;
  /** How the restaurant chose to add the menu (the first pick); the preview offers more of the same. */
  source: PrepSource | null;
  pages: PreparedPage[];
  /** Pages expected (documents count as 1 until they are opened). */
  total: number;
  /** Documents that could not be prepared (the others go on). */
  failed: { name: string; message: string }[];
  /** What stops the import (page limit, nothing usable, upload failure). */
  error: string;
  uploaded: number;
  /** Key of the page being turned or photographed again in the preview. */
  turning: string;
  /** Preparation work of all pages (ms): the benchmark's T1, reported when the import is submitted. */
  ms: number;
}

const INITIAL: PrepState = {
  phase: "idle",
  source: null,
  pages: [],
  total: 0,
  failed: [],
  error: "",
  uploaded: 0,
  turning: "",
  ms: 0,
};

/** Uploads in flight at once: enough to hide the round trip, few enough for a phone's upload link. */
const PARALLEL_UPLOADS = 2;

export const includedPages = (s: PrepState) => s.pages.filter((p) => p.include && p.image);

/** Pages the restaurant should look at before sending: unclear or failed pages, or a document left out. */
export const needsAttention = (s: PrepState) =>
  s.failed.length > 0 || s.pages.some((p) => p.status === "failed" || (p.include && p.status === "unclear"));

class Cancelled extends Error {}

const limitMessage = (pages: string) =>
  `Documentele au ${pages} de pagini; un import are cel mult ${LIMITS.importFilesPerJob}. Împarte meniul în două importuri sau elimină paginile de care nu ai nevoie.`;

export function usePagePreparation() {
  const [state, setState] = useState<PrepState>(INITIAL);
  const latest = useRef(state);
  /** Every document added so far, by document index (PreparedPage.doc). */
  const files = useRef<File[]>([]);
  const photos = useRef(0);
  const control = useRef<AbortController | null>(null);
  const prepared = useRef<Promise<PrepState>>(Promise.resolve(INITIAL));

  const update = useCallback((fn: (s: PrepState) => PrepState) => {
    latest.current = fn(latest.current);
    setState(latest.current);
  }, []);

  const clear = useCallback(() => {
    control.current?.abort(new Cancelled());
    control.current = null;
    for (const p of latest.current.pages) if (p.thumb) URL.revokeObjectURL(p.thumb);
    files.current = [];
    photos.current = 0;
    prepared.current = Promise.resolve(INITIAL);
    update(() => INITIAL);
  }, [update]);

  useEffect(
    () => () => {
      control.current?.abort(new Cancelled());
      for (const p of latest.current.pages) if (p.thumb) URL.revokeObjectURL(p.thumb);
    },
    [],
  );

  /** A camera photo gets a readable name in its order ("Fotografia 2.jpg"); phones name them all alike. */
  const named = useCallback((list: File[], source: PrepSource) => {
    if (source !== "camera") return list;
    return list.map((f) => {
      photos.current += 1;
      const ext = f.type === "image/png" ? "png" : f.type === "image/webp" ? "webp" : "jpg";
      return new File([f], `Fotografia ${photos.current}.${ext}`, {
        type: f.type,
        lastModified: f.lastModified,
      });
    });
  }, []);

  /**
   * Adds documents to the preview (the first ones start it). Preparation runs one batch after another; resolves
   * with the state once this batch is prepared (never rejects).
   */
  const add = useCallback(
    (chosen: File[], source: PrepSource = "files"): Promise<PrepState> => {
      if (latest.current.phase === "uploading") return Promise.resolve(latest.current);
      if (latest.current.phase === "error" && !latest.current.pages.length) clear();
      const list = named(chosen, source);
      const ctrl = control.current ?? new AbortController();
      control.current = ctrl;
      const firstDoc = files.current.length;
      files.current = [...files.current, ...list];
      update((s) => ({
        ...s,
        phase: "preparing",
        source: s.source ?? source,
        error: "",
        total: s.pages.length + list.length,
      }));
      prepared.current = prepared.current.then(async () => {
        if (ctrl.signal.aborted) return latest.current;
        const room = LIMITS.importFilesPerJob - latest.current.pages.length;
        let limit = "";
        try {
          const { prepareDocuments } = await import("./pages");
          const before = latest.current.pages.length;
          await prepareDocuments(
            list,
            room,
            ctrl.signal,
            {
              onTotal: (total) => update((s) => ({ ...s, total: before + total })),
              onPage: (page) => update((s) => ({ ...s, pages: [...s.pages, page], ms: s.ms + page.ms })),
              onDocumentError: (name, e) =>
                update((s) => ({ ...s, failed: [...s.failed, { name, message: e.message }] })),
            },
            firstDoc,
          );
        } catch (e) {
          if (ctrl.signal.aborted) return latest.current;
          const { PageLimitError } = await import("./pages");
          limit =
            e instanceof PageLimitError
              ? limitMessage(String(latest.current.pages.length + Number(e.message)))
              : "Pregătirea paginilor a eșuat. Încearcă din nou.";
        }
        update((s) => ({
          ...s,
          phase: s.pages.length ? "ready" : "error",
          error: limit || (s.pages.length ? "" : "Niciun document nu a putut fi pregătit."),
          total: s.pages.length,
        }));
        return latest.current;
      });
      return prepared.current;
    },
    [clear, named, update],
  );

  /** Starts a new preview with these documents (anything prepared before is dropped). */
  const start = useCallback(
    (list: File[], source: PrepSource = "files") => {
      clear();
      return add(list, source);
    },
    [add, clear],
  );

  const toggle = useCallback(
    (key: string) =>
      update((s) => ({
        ...s,
        pages: s.pages.map((p) => (p.key === key && p.image ? { ...p, include: !p.include } : p)),
      })),
    [update],
  );

  /**
   * "Elimină": the page leaves the preview (and the import). Removing the last one starts over: the first choice
   * (camera, photos or file) comes back, as if nothing had been chosen.
   */
  const remove = useCallback(
    (key: string) => {
      const s = latest.current;
      const pages = s.pages.filter((p) => p.key !== key);
      if (!pages.length && s.phase === "ready") return clear();
      const gone = s.pages.find((p) => p.key === key);
      if (gone?.thumb) URL.revokeObjectURL(gone.thumb);
      update((cur) => ({ ...cur, pages, total: pages.length }));
    },
    [clear, update],
  );

  /** Moves a page one place earlier (-1) or later (+1): the import follows the preview's order. */
  const move = useCallback(
    (key: string, delta: -1 | 1) =>
      update((s) => {
        const i = s.pages.findIndex((p) => p.key === key);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= s.pages.length) return s;
        const pages = [...s.pages];
        [pages[i], pages[j]] = [pages[j] as PreparedPage, pages[i] as PreparedPage];
        return { ...s, pages };
      }),
    [update],
  );

  /** Prepares one page again from `file` with `rotation`, in place; `file` may be a new photo of that page. */
  const redo = useCallback(
    async (key: string, file: File, rotation: number, doc: number) => {
      const page = latest.current.pages.find((p) => p.key === key);
      if (!page || latest.current.turning || latest.current.phase === "uploading") return;
      update((s) => ({ ...s, turning: key }));
      try {
        const { preparePageAgain } = await import("./pages");
        const ctrl = control.current ?? new AbortController();
        control.current = ctrl;
        const next = await preparePageAgain(file, { ...page, doc }, rotation, ctrl.signal);
        if (page.thumb) URL.revokeObjectURL(page.thumb);
        update((s) => ({
          ...s,
          turning: "",
          ms: s.ms + next.ms,
          pages: s.pages.map((p) => (p.key === key ? next : p)),
        }));
      } catch (e) {
        update((s) => ({
          ...s,
          turning: "",
          failed:
            e instanceof Error && "code" in e
              ? [...s.failed, { name: file.name, message: e.message }]
              : s.failed,
        }));
      }
    },
    [update],
  );

  /** "Rotește": the page again, a quarter turn further (optional, never required). */
  const rotate = useCallback(
    async (key: string) => {
      const page = latest.current.pages.find((p) => p.key === key);
      const file = page && files.current[page.doc];
      if (page && file) await redo(key, file, (page.rotation + 90) % 360, page.doc);
    },
    [redo],
  );

  /** "Refotografiază": a new photo replaces this page, in the same place. */
  const retake = useCallback(
    async (key: string, photo: File, source: PrepSource = "camera") => {
      const [file] = named([photo], source);
      if (!file) return;
      const doc = files.current.length;
      files.current = [...files.current, file];
      await redo(key, file, 0, doc);
    },
    [named, redo],
  );

  /**
   * Sends the included pages, in the preview's order, to an import that is still uploading; with `retake`, the
   * first included page replaces that page of a finished import ("Refotografiază pagina").
   */
  const upload = useCallback(
    async (api: ReturnType<typeof importApi>, jobId: string, retake?: { fileId: string; page: number }) => {
      const ctrl = new AbortController();
      control.current = ctrl;
      const pages = includedPages(latest.current).slice(0, retake ? 1 : undefined);
      update((s) => ({ ...s, phase: "uploading", uploaded: 0, error: "" }));
      // A PDF page keeps its number in the PDF; a photo is numbered in the order of the photos ("pagina 3" in
      // Verifică is the third photo). A retaken page keeps its number.
      let photo = 0;
      const numbers = pages.map((p) => (retake ? retake.page : p.kind === "pdf" ? p.page : ++photo));
      try {
        await mapLimit(pages, PARALLEL_UPLOADS, async (p, position) => {
          ctrl.signal.throwIfAborted();
          await api.uploadPage(
            jobId,
            {
              position,
              name: p.name,
              page: numbers[position] ?? 1,
              image: p.image as Blob,
              views: p.views,
              text: p.text,
              sourceSha256: p.sourceSha256,
              prep: {
                ms: p.ms,
                status: p.status,
                reasons: p.reasons.slice(0, 6),
                ink: p.ink,
                textChars: p.textChars,
                width: p.width,
                height: p.height,
              },
            },
            ctrl.signal,
            retake?.fileId,
          );
          update((s) => ({ ...s, uploaded: s.uploaded + 1 }));
        });
      } catch (e) {
        if (ctrl.signal.aborted) throw new Cancelled();
        update((s) => ({ ...s, phase: "ready" }));
        throw e;
      }
    },
    [update],
  );

  const cancel = useCallback(() => {
    control.current?.abort(new Cancelled());
    control.current = null;
  }, []);

  return {
    state,
    start,
    add,
    /** Resolves when the current preparation is over (at once when nothing is being prepared). */
    whenPrepared: () => prepared.current,
    /** The state right now (handlers created before the last render read it here). */
    snapshot: () => latest.current,
    toggle,
    remove,
    move,
    rotate,
    retake,
    upload,
    cancel,
    clear,
  };
}

export const isCancelled = (e: unknown) => e instanceof Cancelled;
